import type { SynthLayer } from "./types";

export type AudioEngineState = "locked" | "running" | "suspended" | "unsupported" | "error";

export interface PlaybackHandle {
  stop(): void;
}

const NOISE_BUFFER_SECONDS = 2;
const MASTER_COMPRESSOR_THRESHOLD_DB = -12;
const MASTER_COMPRESSOR_RATIO = 6;
const MASTER_COMPRESSOR_ATTACK_S = 0.003;
const MASTER_COMPRESSOR_RELEASE_S = 0.15;

/** The QA audition volume slider's default and ceiling - a moderate starting level with real headroom below the master limiter, never the final product's own volume setting (spec section 5/7). */
export const DEFAULT_MASTER_VOLUME = 0.6;
export const MAX_MASTER_VOLUME = 0.85;

/**
 * The only module in this feature that touches real Web Audio (see
 * PR13-D spec section 6/7's "prototype audio implementation" and "audio
 * unlock and safety" rules). Deliberately isolated from every pure data
 * module (`palettes.ts`, `sequences.ts`, `SequenceScheduler.ts`) so those
 * stay unit-testable in this project's Node test environment, which has no
 * `AudioContext`; this class itself is instead verified through a real
 * browser QA pass - the same split `GameScene`/`VisualFeedbackController`
 * already use for the parts of this codebase Phaser/DOM makes impossible
 * to unit test directly.
 *
 * Exactly one `AudioContext` for the page's lifetime, created lazily on
 * the first `unlock()` call (must run inside a real user-gesture handler -
 * the "ENABLE AUDIO" button) and never recreated by a later call. Every
 * node `playRecipe()` creates is explicitly stopped and disconnected, so
 * nothing is ever left connected after its sound finishes (spec section
 * 6's "leave nodes connected after playback" ban) - reusing a single
 * shared white-noise `AudioBuffer` (spec's "pre-rendered reusable
 * AudioBuffers") rather than generating fresh noise data per play.
 */
export class AudioEngine {
  private context: AudioContext | null = null;
  private masterGain: GainNode | null = null;
  private limiter: DynamicsCompressorNode | null = null;
  private noiseBuffer: AudioBuffer | null = null;
  private readonly activeVoices = new Set<PlaybackHandle>();
  private readonly stateChangeListeners = new Set<(state: AudioEngineState) => void>();
  private unsupported = false;
  private lastError: unknown = null;
  private masterVolume = DEFAULT_MASTER_VOLUME;

  getState(): AudioEngineState {
    if (this.unsupported) return "unsupported";
    if (this.lastError) return "error";
    if (!this.context) return "locked";
    if (this.context.state === "running") return "running";
    if (this.context.state === "suspended") return "suspended";
    return "locked";
  }

  /** Subscribes to state changes (unlock completing, the browser auto-suspending a background tab, ...) - returns an unsubscribe function. `AudioLabScene` uses this to keep its state readout live. */
  onStateChange(cb: (state: AudioEngineState) => void): () => void {
    this.stateChangeListeners.add(cb);
    return () => this.stateChangeListeners.delete(cb);
  }

  private notifyStateChange(): void {
    const state = this.getState();
    for (const cb of this.stateChangeListeners) cb(state);
  }

  /**
   * Creates the single `AudioContext` on first call, or resumes it if it
   * already exists and is suspended. Always safe to call again - idempotent
   * per spec section 7: a second call while already running/unsupported/
   * errored is a pure no-op. Never throws; an unsupported browser or a
   * construction failure just leaves `getState()` reporting the right thing
   * instead of crashing the caller.
   */
  unlock(): void {
    if (this.unsupported || this.lastError) return;
    try {
      if (!this.context) {
        const Ctor = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
        if (!Ctor) {
          this.unsupported = true;
          this.notifyStateChange();
          return;
        }
        const ctx = new Ctor();
        this.context = ctx;
        ctx.onstatechange = () => this.notifyStateChange();
        this.masterGain = ctx.createGain();
        this.masterGain.gain.value = this.masterVolume;
        this.limiter = ctx.createDynamicsCompressor();
        this.limiter.threshold.value = MASTER_COMPRESSOR_THRESHOLD_DB;
        this.limiter.ratio.value = MASTER_COMPRESSOR_RATIO;
        this.limiter.attack.value = MASTER_COMPRESSOR_ATTACK_S;
        this.limiter.release.value = MASTER_COMPRESSOR_RELEASE_S;
        this.masterGain.connect(this.limiter);
        this.limiter.connect(ctx.destination);
        this.notifyStateChange();
        return;
      }
      if (this.context.state === "suspended") {
        void this.context.resume().then(() => this.notifyStateChange());
      }
    } catch (err) {
      this.lastError = err;
      this.notifyStateChange();
    }
  }

  setMasterVolume(volume: number): void {
    this.masterVolume = Math.min(MAX_MASTER_VOLUME, Math.max(0, volume));
    if (this.masterGain) this.masterGain.gain.value = this.masterVolume;
  }

  getMasterVolume(): number {
    return this.masterVolume;
  }

  /** Suspends the context in place without discarding it - used for tab-hide safety (spec section 7). A later `unlock()` call resumes the SAME context; this never creates a second one. */
  suspend(): void {
    if (this.context && this.context.state === "running") {
      void this.context.suspend().then(() => this.notifyStateChange());
    }
  }

  private getNoiseBuffer(ctx: AudioContext): AudioBuffer {
    if (!this.noiseBuffer) {
      const length = Math.floor(ctx.sampleRate * NOISE_BUFFER_SECONDS);
      const buffer = ctx.createBuffer(1, length, ctx.sampleRate);
      const data = buffer.getChannelData(0);
      for (let i = 0; i < length; i++) data[i] = Math.random() * 2 - 1;
      this.noiseBuffer = buffer;
    }
    return this.noiseBuffer;
  }

  /**
   * Plays one recipe, returning a handle that stops and disconnects it
   * early. Does nothing (returns an inert handle) if the engine isn't
   * unlocked/running yet, or if anything in here throws - a synthesis bug
   * or an unsupported node type must never crash the QA Scene around it
   * (spec section 13's "Unsupported/failed audio never crashes the QA
   * Scene").
   */
  playRecipe(recipe: SynthLayer, options?: { transposeSemitones?: number }): PlaybackHandle {
    const ctx = this.context;
    const masterGain = this.masterGain;
    if (!ctx || !masterGain || ctx.state !== "running") return { stop: () => {} };

    try {
      const now = ctx.currentTime;
      const durationS = recipe.durationMs / 1000;
      const transposeRatio = options?.transposeSemitones ? Math.pow(2, options.transposeSemitones / 12) : 1;

      const voiceGain = ctx.createGain();
      voiceGain.gain.value = 0;
      const panner = ctx.createStereoPanner();
      panner.pan.value = recipe.pan;
      voiceGain.connect(panner);
      panner.connect(masterGain);

      const sourceNodes: Array<OscillatorNode | AudioBufferSourceNode> = [];

      for (const tone of recipe.tones) {
        const osc = ctx.createOscillator();
        osc.type = tone.shape;
        osc.frequency.setValueAtTime(Math.max(1, tone.startFreqHz * transposeRatio), now);
        osc.frequency.linearRampToValueAtTime(Math.max(1, tone.endFreqHz * transposeRatio), now + durationS);
        const layerGain = ctx.createGain();
        layerGain.gain.value = tone.mix;
        osc.connect(layerGain);
        layerGain.connect(voiceGain);
        osc.start(now);
        osc.stop(now + durationS + 0.05);
        sourceNodes.push(osc);
      }

      if (recipe.noise) {
        const src = ctx.createBufferSource();
        src.buffer = this.getNoiseBuffer(ctx);
        src.loop = true;
        const filter = ctx.createBiquadFilter();
        filter.type = recipe.noise.filterType;
        filter.frequency.value = recipe.noise.filterFreqHz;
        filter.Q.value = recipe.noise.filterQ;
        const layerGain = ctx.createGain();
        layerGain.gain.value = recipe.noise.mix;
        src.connect(filter);
        filter.connect(layerGain);
        layerGain.connect(voiceGain);
        src.start(now);
        src.stop(now + durationS + 0.05);
        sourceNodes.push(src);
      }

      const env = recipe.envelope;
      const attackEndS = now + env.attackMs / 1000;
      const decayEndS = attackEndS + env.decayMs / 1000;
      const sustainEndS = decayEndS + env.sustainMs / 1000;
      const releaseEndS = sustainEndS + env.releaseMs / 1000;
      voiceGain.gain.setValueAtTime(0, now);
      voiceGain.gain.linearRampToValueAtTime(env.peakGain, attackEndS);
      voiceGain.gain.linearRampToValueAtTime(env.peakGain * env.sustainLevel, decayEndS);
      voiceGain.gain.setValueAtTime(env.peakGain * env.sustainLevel, sustainEndS);
      voiceGain.gain.linearRampToValueAtTime(0, releaseEndS);

      const shimmerNodes: Array<DelayNode | GainNode> = [];
      if (recipe.delayTaps) {
        for (const tap of recipe.delayTaps) {
          const delay = ctx.createDelay(2);
          delay.delayTime.value = tap.delayMs / 1000;
          const tapGain = ctx.createGain();
          tapGain.gain.value = tap.gain;
          voiceGain.connect(delay);
          delay.connect(tapGain);
          tapGain.connect(panner);
          shimmerNodes.push(delay, tapGain);
        }
      }

      let stopped = false;
      const cleanup = (): void => {
        if (stopped) return;
        stopped = true;
        this.activeVoices.delete(handle);
        for (const node of sourceNodes) {
          try {
            node.stop();
          } catch {
            // already stopped naturally - fine.
          }
          node.disconnect();
        }
        voiceGain.disconnect();
        panner.disconnect();
        for (const node of shimmerNodes) node.disconnect();
      };

      const totalMs = env.attackMs + env.decayMs + env.sustainMs + env.releaseMs + 80;
      const autoCleanupTimer = setTimeout(cleanup, totalMs);

      const handle: PlaybackHandle = {
        stop: () => {
          clearTimeout(autoCleanupTimer);
          try {
            const rampNow = ctx.currentTime;
            voiceGain.gain.cancelScheduledValues(rampNow);
            voiceGain.gain.setValueAtTime(voiceGain.gain.value, rampNow);
            voiceGain.gain.linearRampToValueAtTime(0, rampNow + 0.03);
          } catch {
            // fall through to immediate cleanup below regardless.
          }
          setTimeout(cleanup, 40);
        },
      };
      this.activeVoices.add(handle);
      return handle;
    } catch (err) {
      this.lastError = err;
      this.notifyStateChange();
      return { stop: () => {} };
    }
  }

  /** Stops every currently-playing voice immediately - the `STOP ALL` button, and part of scene shutdown/tab-hide handling. Never throws. */
  stopAll(): void {
    for (const voice of [...this.activeVoices]) {
      try {
        voice.stop();
      } catch {
        // ignore - a voice that already finished/failed is not this call's problem.
      }
    }
    this.activeVoices.clear();
  }

  /** Full teardown - scene shutdown. Stops everything and closes the context; a later `unlock()` (there won't be one - the scene is gone) would simply build a fresh context, never reuse a closed one. */
  destroy(): void {
    this.stopAll();
    if (this.context) {
      void this.context.close().catch(() => {});
    }
    this.context = null;
    this.masterGain = null;
    this.limiter = null;
    this.noiseBuffer = null;
    this.stateChangeListeners.clear();
  }
}
